/**
 * The error contract, client side.
 *
 * The backend normalises every failure to `{"detail": {code, message, params?}}`
 * (app/core/errors.py). This module turns that into a typed object the UI can branch on,
 * and never shows a raw backend string when a localized message exists for the code.
 */

export type ErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'TOKEN_EXPIRED'
  | 'NOT_AUTHENTICATED'
  | 'ACCOUNT_TEMPORARILY_LOCKED'
  | 'ACCOUNT_DISABLED'
  | 'FORCE_CHANGE_PWD'
  | 'FORBIDDEN'
  | 'ADMIN_REQUIRED'
  | 'API_KEY_SCOPE_DENIED'
  | 'FEATURE_DISABLED'
  | 'FEATURE_LIMIT_REACHED'
  | 'CREDIT_LIMIT_REACHED'
  | 'NOT_FOUND'
  | 'EXPIRED'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'PAYLOAD_TOO_LARGE'
  | 'UNSUPPORTED_MEDIA'
  | 'UPSTREAM_ERROR'
  | 'NOT_CONFIGURED'
  | 'INTERNAL_ERROR'
  | 'NETWORK_ERROR'
  | 'TIMEOUT';

export interface ApiErrorParams {
  limit?: number | null;
  locked_until?: string | null;
  field?: string | null;
  expected?: number;
  actual?: number;
  missing?: string[];
  limit_mb?: number;
  job_id?: string;
  errors?: { field: string; message: string; type: string }[];
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly params: ApiErrorParams;
  readonly raw: unknown;

  constructor(opts: {
    status: number;
    code: ErrorCode;
    message: string;
    params?: ApiErrorParams;
    raw?: unknown;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.params = opts.params ?? {};
    this.raw = opts.raw;
  }

  /** Retrying this request on its own could succeed. */
  get isRetryable(): boolean {
    return (
      this.code === 'NETWORK_ERROR' ||
      this.code === 'TIMEOUT' ||
      this.code === 'UPSTREAM_ERROR' ||
      this.status >= 500
    );
  }

  /** The session, not the request, is the problem. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

const STATUS_FALLBACK: Record<number, ErrorCode> = {
  400: 'VALIDATION_ERROR',
  401: 'NOT_AUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'PAYLOAD_TOO_LARGE',
  415: 'UNSUPPORTED_MEDIA',
  422: 'VALIDATION_ERROR',
  423: 'ACCOUNT_TEMPORARILY_LOCKED',
  429: 'CREDIT_LIMIT_REACHED',
  502: 'UPSTREAM_ERROR',
  503: 'NOT_CONFIGURED',
};

/**
 * Parse whatever a response body turned out to be.
 *
 * Handles the current contract, the pre-rebuild shapes (`detail` as a bare string, or a dict
 * with top-level extras) and a body that is not JSON at all, because the legacy crawler
 * routes still answer in their own envelope.
 */
export function parseApiError(status: number, body: unknown): ApiError {
  const fallbackCode = STATUS_FALLBACK[status] ?? 'INTERNAL_ERROR';

  if (body && typeof body === 'object') {
    const detail = (body as { detail?: unknown }).detail;

    if (detail && typeof detail === 'object') {
      const d = detail as Record<string, unknown>;
      const { code, message, params, ...extras } = d;
      return new ApiError({
        status,
        code: (typeof code === 'string' ? code : fallbackCode) as ErrorCode,
        message: typeof message === 'string' ? message : 'Đã xảy ra lỗi.',
        params: { ...(params as ApiErrorParams), ...extras },
        raw: body,
      });
    }

    if (typeof detail === 'string') {
      return new ApiError({ status, code: fallbackCode, message: detail, raw: body });
    }

    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string') {
      return new ApiError({ status, code: fallbackCode, message, raw: body });
    }
  }

  return new ApiError({
    status,
    code: fallbackCode,
    message: `Lỗi máy chủ (${status})`,
    raw: body,
  });
}

export function networkError(cause?: unknown): ApiError {
  return new ApiError({
    status: 0,
    code: 'NETWORK_ERROR',
    message: 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.',
    raw: cause,
  });
}

export function timeoutError(): ApiError {
  return new ApiError({
    status: 0,
    code: 'TIMEOUT',
    message: 'Yêu cầu quá thời gian chờ.',
  });
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
